// «Креативы» → пул (этап 4, DESIGN §6): сетка вертикальных превью со статусами, видео по наведению,
// батчи агента. Главное действие — «Новый батч». Одна полоса фильтров, как на других страницах: статус,
// оффер, автопроверка, источник (когда роликов из разных источников), поиск и порядок; у каждого фильтра
// счётчики — с учётом остальных фильтров.
import { Button, Chip, EmptyState, Segmented, Skeleton, toast, usd } from '@autoaff/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import {
  ArrowUpDown,
  Clapperboard,
  MessageSquare,
  Play,
  Plus,
  RotateCcw,
  Scale,
  Search,
  Smartphone,
} from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api';
import {
  compareBy,
  FilterBar,
  type FilterOption,
  FilterSelect,
  ResetFilters,
  useRemembered,
} from '../../filters';
import { SidePanel } from '../../shell/SidePanel';
import { PageHead } from '../common';
import { CreativeTabs } from './CreativeTabs';
import {
  type BatchRow,
  type CreativeRow,
  DELIVERY,
  FILTERS,
  mediaUrl,
  QC,
  statusOf,
  tc,
  useCreatives,
  voiceLine,
} from './data';
import { ReviewMode } from './Review';
import './creatives.css';

const BATCH_STATUS: Record<
  BatchRow['status'],
  { label: string; tone: 'default' | 'info' | 'accent' | 'success' }
> = {
  planned: { label: 'В очереди', tone: 'info' },
  in_progress: { label: 'Агент делает', tone: 'info' },
  review: { label: 'Ждёт приёмки', tone: 'accent' },
  done: { label: 'Готов', tone: 'success' },
  cancelled: { label: 'Отменён', tone: 'default' },
};

/** Карточка ролика в сетке: постер, видео по наведению мышью, статус, длина. */
function CreoCard({ c }: { c: CreativeRow }) {
  const video = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const st = statusOf(c.status);
  const dim = c.status === 'rejected' || c.status === 'archived';
  const play = (on: boolean) => {
    const v = video.current;
    if (!v || !c.version) return;
    if (on) {
      if (!v.src) v.src = mediaUrl(c.version.video) ?? '';
      void v
        .play()
        .then(() => setPlaying(true))
        .catch(() => {});
    } else {
      v.pause();
      setPlaying(false);
    }
  };
  const qc = c.version && c.status === 'qc' ? QC[c.version.qcStatus] : null;
  return (
    <Link
      to="/creatives/$id"
      params={{ id: String(c.id) }}
      className={`creo${playing ? ' playing' : ''}`}
      aria-label={`${c.offerName} ${c.code}${c.fatigue ? ', выгорает' : ''}`}
      title={c.fatigue ? `Выгорает: ${c.fatigue.summary}` : undefined}
      onPointerEnter={(e) => e.pointerType === 'mouse' && play(true)}
      onPointerLeave={(e) => e.pointerType === 'mouse' && play(false)}
    >
      <div className={`thumb${dim ? ' dim' : ''}`}>
        {c.version?.poster ? (
          <img src={mediaUrl(c.version.poster) ?? ''} alt="" loading="lazy" />
        ) : (
          <div className="thumb-empty">
            <Clapperboard />
          </div>
        )}
        <video ref={video} muted loop playsInline preload="none" />
        <span className="thumb-st">
          <Chip tone={st.tone} dot>
            {st.label}
          </Chip>
          {c.fatigue && (
            <Chip tone="warning" dot>
              Выгорает
            </Chip>
          )}
        </span>
        {c.durationSec ? <span className="dur">{tc(c.durationSec)}</span> : null}
        {c.openComments > 0 && (
          <span className="thumb-com" title="Правки ждут агента">
            <MessageSquare aria-hidden />
            {c.openComments}
          </span>
        )}
        <span className="play" aria-hidden>
          <span>
            <Play />
          </span>
        </span>
      </div>
      <div className="creo-b">
        <div className="creo-t">
          <span>{c.offerName}</span>
          <span className="id">{c.code}</span>
        </div>
        <div className="creo-a">{c.angle ?? c.hook ?? c.title ?? '—'}</div>
        <div className="creo-m">
          {qc ? (
            <span className={qc.tone === 'danger' ? 'd' : qc.tone === 'warning' ? 'w' : ''}>{qc.label}</span>
          ) : (
            <span>
              {[c.delivery ? (DELIVERY[c.delivery] ?? c.delivery) : null, voiceLine(c.voice, c.language)]
                .filter(Boolean)
                .join(' · ') || '—'}
            </span>
          )}
          {c.costUsd > 0 && <span>{usd(c.costUsd)}</span>}
        </div>
      </div>
    </Link>
  );
}

function NewBatch({
  open,
  onClose,
  offers,
}: {
  open: boolean;
  onClose: () => void;
  offers: Array<{ id: number; name: string }>;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [offerId, setOfferId] = useState<number>(offers[0]?.id ?? 0);
  const [count, setCount] = useState(6);
  const [mode, setMode] = useState<'autonomous' | 'revisions'>('autonomous');
  const [brief, setBrief] = useState('');
  const [busy, setBusy] = useState(false);
  // Офферы приходят после открытия страницы — выбрать первый, когда список появится
  const firstOffer = offers[0]?.id;
  useEffect(() => {
    if (firstOffer && !offerId) setOfferId(firstOffer);
  }, [firstOffer, offerId]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!offerId) return;
    setBusy(true);
    try {
      const r = await api<{ id: number; threadId: string }>('/api/creatives/batches', {
        offerId,
        count,
        mode,
        ...(brief.trim() ? { brief: brief.trim() } : {}),
      });
      await qc.invalidateQueries({ queryKey: ['creatives'] });
      toast('Батч в очереди агента — ход работы виден в треде');
      onClose();
      void navigate({ to: '/agent/chats/$threadId', params: { threadId: r.threadId } });
    } catch (err) {
      toast((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <SidePanel
      open={open}
      label="Новый батч"
      onClose={onClose}
      head={<div className="sp-title">Новый батч</div>}
    >
      <form className="form-stack" onSubmit={submit}>
        <p className="muted">
          Агент сделает ролики по вашей методике: профиль товара, матрица, сценарии, футаж и генерации,
          монтаж, проверка. Бюджет — до $3 на ролик, цель — вдвое меньше.
        </p>
        <label className="label" htmlFor="nb-offer">
          Оффер
        </label>
        <select
          id="nb-offer"
          className="input"
          value={offerId}
          onChange={(e) => setOfferId(Number(e.target.value))}
        >
          {offers.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
        <label className="label" htmlFor="nb-count">
          Сколько роликов
        </label>
        <input
          id="nb-count"
          className="input"
          type="number"
          min={1}
          max={20}
          value={count}
          onChange={(e) => setCount(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
        />
        <span className="label">Как работать</span>
        <Segmented
          label="Режим батча"
          value={mode}
          onChange={setMode}
          options={[
            { value: 'autonomous', label: 'Сам до конца' },
            { value: 'revisions', label: 'С моими правками' },
          ]}
        />
        <label className="label" htmlFor="nb-brief">
          Пожелания (необязательно)
        </label>
        <textarea
          id="nb-brief"
          className="input textarea"
          rows={4}
          placeholder="Например: больше роликов с руками крупно, один на русском, цена 89 000"
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
        />
        <Button type="submit" variant="primary" block disabled={busy || !offerId}>
          Запустить батч
        </Button>
      </form>
    </SidePanel>
  );
}

type QcFilter = 'all' | 'passed' | 'warn' | 'failed' | 'waiting';
const QC_FILTERS: Array<{ value: QcFilter; label: string }> = [
  { value: 'all', label: 'Все' },
  { value: 'passed', label: 'Пройдена' },
  { value: 'warn', label: 'Есть замечания' },
  { value: 'failed', label: 'Не пройдена' },
  { value: 'waiting', label: 'Ждёт проверки' },
];
/** Ролик под фильтр автопроверки: «ждёт проверки» — и проверяется сейчас, и ещё без версии (черновик). */
const qcMatch = (c: CreativeRow, f: QcFilter) =>
  f === 'all' ||
  (f === 'waiting'
    ? !c.version || c.version.qcStatus === 'pending' || c.version.qcStatus === 'running'
    : c.version?.qcStatus === f);

/** Откуда ролик: сделал агент на платформе или перенесён из Creatives Room. */
const SOURCE_RU: Record<string, string> = { factory: 'Сделал агент', creatives_room: 'Creatives Room' };

type PoolSort = 'new' | 'recent' | 'offer';
const SORTS: Array<FilterOption<PoolSort>> = [
  { value: 'new', label: 'Сначала новые' },
  { value: 'recent', label: 'Недавно менялись' },
  { value: 'offer', label: 'По офферу' },
];
const SORT_KEYS = SORTS.map((x) => x.value);
const codeNum = (code: string) => Number(/\d+/.exec(code)?.[0] ?? 0);
const ORDER: Record<PoolSort, (a: CreativeRow, b: CreativeRow) => number> = {
  new: compareBy((c: CreativeRow) => c.createdAt ?? c.updatedAt, 'desc'),
  recent: compareBy((c: CreativeRow) => c.updatedAt, 'desc'),
  // По офферу — внутри оффера по номеру ролика: c1, c2 … c10
  offer: (a, b) => a.offerName.localeCompare(b.offerName, 'ru') || codeNum(a.code) - codeNum(b.code),
};

/** Батч «в работе», а агент над ним не работает — работа оборвалась, её можно продолжить. */
const stalled = (b: BatchRow) => (b.status === 'planned' || b.status === 'in_progress') && !b.working;

function Batches({ list }: { list: BatchRow[] }) {
  if (!list.length) return null;
  return (
    <section className="batches" aria-label="Батчи">
      {list.slice(0, 4).map((b) => {
        const st = stalled(b) ? { label: 'Остановился', tone: 'warning' as const } : BATCH_STATUS[b.status];
        return (
          <Link key={b.id} to="/creatives" search={{ batch: b.id }} className="batch-row">
            <div className="batch-main">
              <b>{b.offerName}</b>
              <span className="muted">
                Батч: {b.made} из {b.targetCount} · {b.mode === 'revisions' ? 'с правками' : 'сам'}
              </span>
            </div>
            <Chip tone={st.tone} dot>
              {st.label}
            </Chip>
          </Link>
        );
      })}
    </section>
  );
}

export function CreativesPage() {
  const q = useCreatives();
  const { batch } = useSearch({ from: '/creatives' });
  const navigate = useNavigate({ from: '/creatives' });
  const [filter, setFilter] = useState('all');
  const [offer, setOffer] = useState('all');
  const [check, setCheck] = useState<QcFilter>('all');
  const [source, setSource] = useState('all');
  const [text, setText] = useState('');
  const [sort, setSort] = useRemembered<PoolSort>('creatives:sort', 'new', SORT_KEYS);
  const [creating, setCreating] = useState(false);
  const [review, setReview] = useState(false);
  const data = q.data;
  const f = FILTERS.find((x) => x.key === filter) ?? FILTERS[0];
  // Батч и поиск сужают всё; остальные фильтры считают счётчики друг для друга
  const base = useMemo(() => {
    const t = text.trim().toLowerCase();
    return (data?.creatives ?? []).filter(
      (c) =>
        (!batch || c.batchId === batch) &&
        (!t ||
          [c.offerName, c.code, c.angle, c.hook, c.voice, c.title].some((x) => x?.toLowerCase().includes(t))),
    );
  }, [data, text, batch]);
  const by = {
    offer: (c: CreativeRow) => offer === 'all' || c.offerSlug === offer,
    status: (c: CreativeRow) => f?.match(c.status, c) ?? true,
    check: (c: CreativeRow) => qcMatch(c, check),
    source: (c: CreativeRow) => source === 'all' || c.source === source,
  };
  const except = (skip?: keyof typeof by) =>
    base.filter((c) => (Object.keys(by) as Array<keyof typeof by>).every((k) => k === skip || by[k](c)));
  const list = except().sort(ORDER[sort]);
  const statusOpts = FILTERS.map((x) => ({
    value: x.key,
    label: x.label,
    count: except('status').filter((c) => x.match(c.status, c)).length,
  }));
  const offerBase = except('offer');
  const checkBase = except('check');
  const sourceBase = except('source');
  const sources = [...new Set((data?.creatives ?? []).map((c) => c.source))];
  const filtered =
    filter !== 'all' || offer !== 'all' || check !== 'all' || source !== 'all' || text.trim() !== '';
  const reset = () => {
    setFilter('all');
    setOffer('all');
    setCheck('all');
    setSource('all');
    setText('');
    setSort('new');
  };
  const batchRow = batch ? data?.batches.find((b) => b.id === batch) : undefined;
  const qc = useQueryClient();
  const go = useNavigate();
  const compareBatch = async (id: number) => {
    try {
      const r = await api<{ id: number }>('/api/compare', { batchId: id });
      void go({ to: '/creatives/compare/$id', params: { id: String(r.id) } });
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const continueBatch = async (id: number) => {
    try {
      await api(`/api/creatives/batches/${id}/continue`, {});
      toast('Агент продолжит батч с того места, где остановился — без повторных трат');
      void qc.invalidateQueries({ queryKey: ['creatives'] });
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const withCreatives = (data?.offers ?? []).filter((o) => o.n > 0);
  const total = data?.creatives.length ?? 0;

  return (
    <>
      <PageHead
        title="Креативы"
        sub={
          data
            ? `Пул · ${total} ${total === 1 ? 'ролик' : total % 10 >= 2 && total % 10 <= 4 && (total < 10 || total > 20) ? 'ролика' : 'роликов'} в ${withCreatives.length} офферах`
            : 'Пул роликов'
        }
        actions={
          <>
            {/* Главный сценарий на телефоне — листать ролики как в ленте, поэтому кнопка есть везде */}
            <Button icon={<Smartphone />} onClick={() => setReview(true)} disabled={!list.length}>
              Смотреть как Reels
            </Button>
            <Button
              variant="primary"
              icon={<Plus />}
              onClick={() => setCreating(true)}
              disabled={!data?.offers.length}
            >
              Новый батч
            </Button>
          </>
        }
      />
      <CreativeTabs active="/creatives" />
      <div className="pc">
        {q.isPending ? (
          <div className="cgrid" aria-busy>
            {Array.from({ length: 10 }, (_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: скелетоны без данных
              <div key={i} className="creo">
                <Skeleton className="thumb" h={0} />
                <Skeleton w="70%" />
                <Skeleton w="90%" h={12} />
              </div>
            ))}
          </div>
        ) : q.isError ? (
          <div className="card">
            <EmptyState
              icon={<Clapperboard />}
              title="Не удалось загрузить креативы"
              action={<Button onClick={() => q.refetch()}>Повторить</Button>}
            >
              {(q.error as Error).message}
            </EmptyState>
          </div>
        ) : !total ? (
          <div className="card">
            <EmptyState
              icon={<Clapperboard />}
              title="Роликов пока нет"
              action={
                <Button
                  variant="primary"
                  icon={<Plus />}
                  onClick={() => setCreating(true)}
                  disabled={!data?.offers.length}
                >
                  Запустить первый батч
                </Button>
              }
            >
              Попросите агента сделать батч: он напишет сценарии, найдёт футаж, озвучит, смонтирует и проверит
              ролики. Готовые появятся здесь.
            </EmptyState>
          </div>
        ) : (
          <>
            {batch ? (
              <div className="batch-filter">
                <span>
                  Батч <b>{batchRow?.title ?? `#${batch}`}</b>
                  {batchRow
                    ? stalled(batchRow)
                      ? ` · остановился на ${batchRow.made} из ${batchRow.targetCount}`
                      : ` · ${BATCH_STATUS[batchRow.status].label.toLowerCase()}`
                    : ''}
                </span>
                {batchRow && stalled(batchRow) && (
                  <Button
                    size="sm"
                    variant="primary"
                    icon={<RotateCcw />}
                    onClick={() => void continueBatch(batchRow.id)}
                  >
                    Продолжить батч
                  </Button>
                )}
                {batchRow && (batchRow.status === 'review' || batchRow.status === 'done') && (
                  <Button size="sm" icon={<Scale />} onClick={() => void compareBatch(batchRow.id)}>
                    Сравнить вслепую с прежними
                  </Button>
                )}
                <Button size="sm" onClick={() => void navigate({ search: {} })}>
                  Показать все ролики
                </Button>
              </div>
            ) : (
              <Batches list={data?.batches ?? []} />
            )}
            <FilterBar
              active={
                [filter !== 'all', offer !== 'all', check !== 'all', source !== 'all'].filter(Boolean).length
              }
              search={
                <label className="search-field filter-end">
                  <Search aria-hidden />
                  <input
                    type="search"
                    placeholder="Угол, голос, код ролика"
                    aria-label="Найти ролик: угол, хук, голос, код"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                  />
                </label>
              }
              end={
                <FilterSelect
                  label="Порядок"
                  value={sort}
                  onChange={setSort}
                  options={SORTS}
                  allValue="new"
                  icon={<ArrowUpDown aria-hidden />}
                  width={220}
                  showValue
                />
              }
            >
              <FilterSelect
                label="Статус"
                value={filter}
                onChange={setFilter}
                options={statusOpts}
                allValue="all"
              />
              <FilterSelect
                label="Оффер"
                value={offer}
                onChange={setOffer}
                allValue="all"
                options={[
                  { value: 'all', label: 'Все', count: offerBase.length },
                  ...withCreatives.map((o) => ({
                    value: o.slug,
                    label: o.name,
                    count: offerBase.filter((c) => c.offerSlug === o.slug).length,
                  })),
                ]}
              />
              <FilterSelect
                label="Автопроверка"
                value={check}
                onChange={setCheck}
                allValue="all"
                options={QC_FILTERS.map((x) => ({
                  ...x,
                  count: checkBase.filter((c) => qcMatch(c, x.value)).length,
                })).filter((x) => x.value !== 'waiting' || x.count > 0 || check === 'waiting')}
              />
              {(sources.length > 1 || source !== 'all') && (
                <FilterSelect
                  label="Источник"
                  value={source}
                  onChange={setSource}
                  allValue="all"
                  options={[
                    { value: 'all', label: 'Все', count: sourceBase.length },
                    ...sources.map((x) => ({
                      value: x,
                      label: SOURCE_RU[x] ?? x,
                      count: sourceBase.filter((c) => c.source === x).length,
                    })),
                  ]}
                />
              )}
              <ResetFilters shown={filtered || sort !== 'new'} onClick={reset} />
            </FilterBar>
            {list.length ? (
              <div className="cgrid">
                {list.map((c) => (
                  <CreoCard key={c.id} c={c} />
                ))}
              </div>
            ) : (
              <div className="cempty">
                <p className="muted">Под эти фильтры роликов нет.</p>
                {filtered && (
                  <Button size="sm" onClick={reset}>
                    Сбросить фильтры
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <NewBatch
        open={creating}
        onClose={() => setCreating(false)}
        offers={(data?.offers ?? []).map((o) => ({ id: o.id, name: o.name }))}
      />
      {review && <ReviewMode list={list} onClose={() => setReview(false)} />}
    </>
  );
}
